import React, { useState } from 'react'
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline'
import { GigFilters as GigFiltersType, GigCategory } from '@/types/gig'
import SelectDropdown, { SelectOption } from '@/components/ui/SelectDropdown'

interface GigFiltersProps {
    filters: GigFiltersType
    onFiltersChange: (filters: Partial<GigFiltersType>) => void
    categories: GigCategory[]
    className?: string
}

import { SERVICE_TYPES } from '@/data/serviceTypes';

const PAYMENT_RANGES = [
    { label: 'Any Amount', min: undefined, max: undefined },
    { label: 'Under $25', min: undefined, max: 25 },
    { label: '$25 - $100', min: 25, max: 100 },
    { label: '$100 - $500', min: 100, max: 500 },
    { label: '$500 - $2,000', min: 500, max: 2000 },
    { label: 'Over $2,000', min: 2000, max: undefined }
]

const URGENCY_OPTIONS = [
    { value: '', label: 'Any Urgency' },
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' }
]

const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'pay_high', label: 'Highest Pay' },
    { value: 'pay_low', label: 'Lowest Pay' },
    { value: 'distance', label: 'Nearest to Me' }
]

const GigFilters: React.FC<GigFiltersProps> = ({
    filters,
    onFiltersChange,
    categories,
    className = ''
}) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const handleFilterChange = (key: keyof GigFiltersType, value: any) => {
        onFiltersChange({ [key]: value })
    }

    const handlePaymentRangeChange = (min?: number, max?: number) => {
        onFiltersChange({ minPay: min, maxPay: max })
    }

    const clearAllFilters = () => {
        onFiltersChange({
            query: '',
            category: '',
            subcategory: '',
            location: '',
            serviceType: '',
            minPay: undefined,
            maxPay: undefined,
            paymentType: '',
            urgency: '',
            featured: undefined,
            sortBy: 'newest'
        })
    }

    const hasActiveFilters = !!(
        filters.query ||
        filters.category ||
        filters.subcategory ||
        filters.location ||
        filters.serviceType ||
        filters.minPay ||
        filters.maxPay ||
        filters.paymentType ||
        filters.urgency ||
        filters.featured
    )

    const selectedCategory = categories?.find(cat => cat.slug === filters.category)

    return (
        <div className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}>
            {/* Search Bar */}
            <div className="border-b border-slate-200 p-4 dark:border-gray-700">
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search gigs..."
                        value={filters.query || ''}
                        onChange={(e) => handleFilterChange('query', e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder-gray-400"
                    />
                </div>
            </div>

            {/* Filter Toggle */}
            <div className="border-b border-slate-200 p-4 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                    >
                        <FunnelIcon className="h-5 w-5" />
                        <span>Filters</span>
                        {hasActiveFilters && (
                            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-600 dark:bg-primary-900 dark:text-primary-300">
                                Active
                            </span>
                        )}
                    </button>

                    <div className="flex items-center gap-2">
                        {hasActiveFilters && (
                            <button
                                onClick={clearAllFilters}
                                className="text-xs font-semibold uppercase tracking-wide text-red-600 transition-colors hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                                Clear All
                            </button>
                        )}

                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
                            title={isExpanded ? 'Collapse filters' : 'Expand filters'}
                        >
                            <AdjustmentsHorizontalIcon className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Expanded Filters */}
            {isExpanded && (
                <div className="space-y-5 p-4">
                    {/* Sort By */}
                    <SelectDropdown
                        label="Sort By"
                        value={filters.sortBy || 'newest'}
                        onChange={(value) => handleFilterChange('sortBy', value)}
                        options={SORT_OPTIONS}
                        placeholder="Select sort order"
                    />

                    {/* Category Filter */}
                    <SelectDropdown
                        label="Category"
                        value={filters.category || ''}
                        onChange={(value) => {
                            handleFilterChange('category', value)
                            handleFilterChange('subcategory', '') // Reset subcategory
                        }}
                        options={[
                            { value: '', label: 'All Categories' },
                            ...(categories?.map((category) => ({
                                value: category.slug,
                                label: `${category.icon} ${category.name}`
                            })) || [])
                        ]}
                        placeholder="Select a category"
                    />

                    {/* Subcategory Filter */}
                    {selectedCategory && selectedCategory.subcategories.length > 0 && (
                        <SelectDropdown
                            label="Subcategory"
                            value={filters.subcategory || ''}
                            onChange={(value) => handleFilterChange('subcategory', value)}
                            options={[
                                { value: '', label: 'All Subcategories' },
                                ...selectedCategory.subcategories.map((subcategory) => ({
                                    value: subcategory.slug,
                                    label: subcategory.name
                                }))
                            ]}
                            placeholder="Select a subcategory"
                        />
                    )}

                    {/* Location Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Location
                        </label>
                        <input
                            type="text"
                            value={filters.location || ''}
                            onChange={(e) => handleFilterChange('location', e.target.value)}
                            placeholder="Enter location"
                            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder-gray-400"
                        />
                    </div>

                    {/* Service Type Filter */}
                    <SelectDropdown
                        label="Service Type"
                        value={filters.serviceType || ''}
                        onChange={(value) => handleFilterChange('serviceType', value)}
                        options={[
                            { value: '', label: 'All Service Types' },
                            ...SERVICE_TYPES.map((type) => ({
                                value: type.value,
                                label: `${type.icon} ${type.label}`
                            }))
                        ]}
                        placeholder="Select a service type"
                    />

                    {/* Payment Range */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Payment Range
                        </label>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-1">
                            {PAYMENT_RANGES.map((range, index) => (
                                <label key={index} className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40">
                                    <input
                                        type="radio"
                                        name="paymentRange"
                                        checked={filters.minPay === range.min && filters.maxPay === range.max}
                                        onChange={() => handlePaymentRangeChange(range.min, range.max)}
                                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                        {range.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Payment Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Payment Type
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40">
                                <input
                                    type="radio"
                                    name="paymentType"
                                    value=""
                                    checked={filters.paymentType === ''}
                                    onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                    Any Type
                                </span>
                            </label>
                            <label className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40">
                                <input
                                    type="radio"
                                    name="paymentType"
                                    value="fixed"
                                    checked={filters.paymentType === 'fixed'}
                                    onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                    Fixed Price
                                </span>
                            </label>
                            <label className="flex items-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40">
                                <input
                                    type="radio"
                                    name="paymentType"
                                    value="hourly"
                                    checked={filters.paymentType === 'hourly'}
                                    onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                    Hourly Rate
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Urgency */}
                    <SelectDropdown
                        label="Urgency"
                        value={filters.urgency || ''}
                        onChange={(value) => handleFilterChange('urgency', value)}
                        options={URGENCY_OPTIONS}
                        placeholder="Select urgency level"
                    />

                    {/* Featured Toggle */}
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Featured Gigs Only
                        </label>
                        <button
                            type="button"
                            onClick={() => handleFilterChange('featured', !filters.featured)}
                            aria-label="Toggle featured gigs only"
                            title="Toggle featured gigs only"
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${filters.featured
                                ? 'bg-primary-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                                }`}
                        >
                            <span
                                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${filters.featured ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                            />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default GigFilters